<template>
  <section class="workspace-stack">
    <article class="glass-surface workspace-panel workspace-panel--hero">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Etapas</span>
          <h2 class="workspace-title">Gestion comercial de etapas</h2>
          <p class="workspace-copy">Define precios, stock, checkout, acceso por codigos y campos extra. Las cortesias viven aparte.</p>
        </div>

        <div class="workspace-actions workspace-actions--compact">
          <button class="workspace-button" type="button" @click="toggleCatalogWorkspace">
            <i class="fas fa-sliders"></i>
            <span>Configurar</span>
          </button>
          <button class="workspace-button workspace-button--primary" type="button" @click="openCreatePanel()">
            <i class="fas fa-plus"></i>
            <span>Nueva etapa</span>
          </button>
        </div>
      </div>

      <div class="workspace-metrics">
        <article class="workspace-metric">
          <small>Total etapas</small>
          <strong>{{ stageTickets.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Activas</small>
          <strong>{{ activeStages.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Ocultas</small>
          <strong>{{ hiddenStages.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Campos reutilizables</small>
          <strong>{{ catalogFields.length }}</strong>
        </article>
      </div>
    </article>

    <article v-if="catalogWorkspaceOpen" class="glass-surface workspace-panel workspace-editor-panel">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Configurar</span>
          <h2 class="workspace-title">Campos extra reutilizables</h2>
          <p class="workspace-copy">Administra el catalogo del evento y arma una configuracion lista para aplicar a todas las etapas actuales. Las nuevas etapas futuras no la heredan automaticamente.</p>
        </div>

        <button class="workspace-chip-button" type="button" @click="catalogWorkspaceOpen = false">
          <i class="fas fa-times"></i>
          <span>Cerrar</span>
        </button>
      </div>

      <div class="workspace-two-column">
        <section class="workspace-subpanel">
          <div class="workspace-subpanel__head">
            <div>
              <h3>Catalogo del evento</h3>
              <p>Estos campos se pueden reutilizar en cualquier etapa actual.</p>
            </div>
            <button class="workspace-chip-button" type="button" @click="openCatalogFieldEditor('global')">
              <i class="fas fa-plus"></i>
              <span>Crear campo</span>
            </button>
          </div>

          <div v-if="catalogEditorOpen && catalogEditorContext === 'global'" class="workspace-card workspace-card--soft">
            <div class="workspace-subpanel__head workspace-subpanel__head--tight">
              <div>
                <strong>{{ editingCatalogFieldId ? 'Editar campo reutilizable' : 'Nuevo campo reutilizable' }}</strong>
                <p>Sirve para el checkout y para varias etapas.</p>
              </div>
              <button class="workspace-chip-button" type="button" @click="closeCatalogFieldEditor">
                <i class="fas fa-times"></i>
                <span>Cerrar</span>
              </button>
            </div>

            <form class="workspace-form" @submit.prevent="submitCatalogField">
              <label class="workspace-field">
                <span>Etiqueta</span>
                <input v-model="catalogForm.label" class="workspace-input" type="text" placeholder="Documento" required />
              </label>

              <label class="workspace-field">
                <span>Tipo</span>
                <select v-model="catalogForm.field_type" class="workspace-input workspace-select">
                  <option value="field">Campo libre</option>
                  <option value="multiple">Seleccion unica</option>
                </select>
              </label>

              <label class="workspace-field workspace-field--wide">
                <span>Ayuda</span>
                <input v-model="catalogForm.help_text" class="workspace-input" type="text" placeholder="Se vera debajo del campo en checkout" />
              </label>

              <label v-if="catalogForm.field_type === 'multiple'" class="workspace-field workspace-field--wide">
                <span>Opciones</span>
                <textarea v-model="catalogOptionsDraft" class="workspace-input workspace-input--textarea" rows="3" placeholder="S&#10;M&#10;L"></textarea>
                <small class="workspace-field__note">Una opcion por linea.</small>
              </label>

              <label class="workspace-inline-check workspace-inline-check--single">
                <input v-model="catalogForm.is_active" type="checkbox" />
                <span>Campo activo</span>
              </label>

              <div class="workspace-actions">
                <button class="workspace-button workspace-button--primary" type="submit" :disabled="catalogSaving">
                  <i class="fas fa-save"></i>
                  <span>{{ catalogSaving ? 'Guardando...' : editingCatalogFieldId ? 'Guardar campo' : 'Crear campo' }}</span>
                </button>
              </div>
            </form>
          </div>

          <div v-if="!catalogFields.length" class="workspace-empty">Todavia no hay campos reutilizables.</div>
          <div v-else class="workspace-list-stack">
            <article v-for="field in catalogFields" :key="field.id" class="workspace-card workspace-card--soft">
              <div class="workspace-subpanel__head workspace-subpanel__head--tight">
                <div>
                  <strong>{{ field.label }}</strong>
                  <p>{{ fieldTypeLabel(field.field_type) }} · {{ field.slug }}</p>
                </div>
                <span class="workspace-badge">{{ field.assigned_tickets_count || 0 }} etapas</span>
              </div>

              <p v-if="field.help_text" class="workspace-table-subcopy">{{ field.help_text }}</p>
              <div v-if="Array.isArray(field.options) && field.options.length" class="workspace-chip-row">
                <span v-for="option in field.options" :key="`${field.id}-${option}`" class="workspace-badge">{{ option }}</span>
              </div>

              <div class="workspace-inline-actions">
                <button class="workspace-chip-button" type="button" @click="openCatalogFieldEditor('global', field)">
                  <i class="fas fa-pen"></i>
                  <span>Editar</span>
                </button>
                <button class="workspace-chip-button" type="button" @click="toggleGlobalAssignment(field.id)">
                  <i :class="isFieldSelected(globalAssignments, field.id) ? 'fas fa-minus' : 'fas fa-plus'"></i>
                  <span>{{ isFieldSelected(globalAssignments, field.id) ? 'Quitar de config' : 'Sumar a config' }}</span>
                </button>
                <button class="workspace-chip-button" type="button" @click="deleteCatalogField(field)">
                  <i class="fas fa-trash-alt"></i>
                  <span>Eliminar</span>
                </button>
              </div>
            </article>
          </div>
        </section>

        <section class="workspace-subpanel">
          <div class="workspace-subpanel__head">
            <div>
              <h3>Configuracion global para etapas actuales</h3>
              <p>Arma una combinacion reusable y luego aplicala explicitamente a todas las etapas actuales.</p>
            </div>
          </div>

          <div class="workspace-card workspace-card--soft">
            <div v-if="!globalAssignmentDetails.length" class="workspace-empty workspace-empty--compact">Selecciona campos del catalogo para preparar una configuracion global.</div>
            <div v-else class="workspace-list-stack">
              <article v-for="(assignment, index) in globalAssignmentDetails" :key="`global-${assignment.field_id}`" class="workspace-assignment-card">
                <div>
                  <strong>{{ assignment.label }}</strong>
                  <p>{{ fieldTypeLabel(assignment.field_type) }}</p>
                </div>
                <div class="workspace-assignment-controls">
                  <label class="workspace-inline-check workspace-inline-check--single">
                    <input :checked="assignment.is_required" type="checkbox" @change="toggleAssignmentRequired(globalAssignments, assignment.field_id)" />
                    <span>Obligatorio</span>
                  </label>
                  <div class="workspace-inline-actions">
                    <button class="workspace-chip-button" type="button" :disabled="index === 0" @click="moveAssignment(globalAssignments, index, -1)">
                      <i class="fas fa-arrow-up"></i>
                    </button>
                    <button class="workspace-chip-button" type="button" :disabled="index === globalAssignmentDetails.length - 1" @click="moveAssignment(globalAssignments, index, 1)">
                      <i class="fas fa-arrow-down"></i>
                    </button>
                    <button class="workspace-chip-button" type="button" @click="toggleGlobalAssignment(assignment.field_id)">
                      <i class="fas fa-minus"></i>
                      <span>Quitar</span>
                    </button>
                  </div>
                </div>
              </article>
            </div>

            <div class="workspace-actions workspace-actions--split-top">
               <button class="workspace-button workspace-button--primary" type="button" :disabled="applyingGlobalAssignments || !stageTickets.length" @click="applyGlobalAssignmentsToCurrentStages">
                <i class="fas fa-layer-group"></i>
                <span>{{ applyingGlobalAssignments ? 'Aplicando...' : 'Aplicar a todas las etapas actuales' }}</span>
              </button>
            </div>
            <p class="workspace-field__note">Esta accion actualiza solo las etapas que ya existen en este evento ahora mismo.</p>
          </div>

          <div class="workspace-card workspace-card--soft">
            <div class="workspace-subpanel__head workspace-subpanel__head--tight">
              <div>
                <strong>Preview checkout</strong>
                <p>Asi se verian estos campos dentro de una etapa.</p>
              </div>
            </div>
            <div class="workspace-preview-stack">
              <template v-if="globalAssignmentDetails.length">
                <div v-for="field in globalAssignmentDetails" :key="`global-preview-${field.field_id}`" class="workspace-preview-field">
                  <label>
                    {{ field.label }}
                    <span v-if="field.is_required">*</span>
                  </label>
                  <input v-if="field.field_type === 'field'" class="workspace-input" type="text" :placeholder="field.help_text || 'Respuesta del invitado'" disabled />
                  <select v-else class="workspace-input workspace-select" disabled>
                    <option value="">Selecciona una opcion</option>
                    <option v-for="option in field.options" :key="`${field.field_id}-global-preview-${option}`" :value="option">{{ option }}</option>
                  </select>
                  <small v-if="field.help_text" class="workspace-field__note">{{ field.help_text }}</small>
                </div>
              </template>
              <div v-else class="workspace-empty workspace-empty--compact">Agrega al menos un campo para ver el preview.</div>
            </div>
          </div>
        </section>
      </div>
    </article>

    <WorkspaceWizardModal
      :model-value="editorOpen"
      kicker="Etapas"
      :title="editingTicketId ? 'Editar etapa' : 'Nueva etapa'"
      :copy="editingTicketId ? '' : ''"
      size="xl"
      @close-request="resetForm"
    >
      <form id="stage-editor-form" class="stage-modal" @submit.prevent="submitStage">
        <section class="stage-modal__section">
          <div class="stage-modal__section-head">
            <div>
              <span class="workspace-kicker">Base</span>
              <h3>Datos base</h3>
            </div>
          </div>

          <div class="stage-base-facts">
            <span class="workspace-badge workspace-badge--mint">{{ stageTaxPreview.grossAmount > 0 ? `$${formatMoney(stageTaxPreview.grossAmount)}` : '$0.00' }}</span>
            <span class="workspace-badge">{{ Number(form.initial_quantity) || 0 }} boletos</span>
            <span class="workspace-badge">{{ Number(form.max_quantity) || 0 }} por compra</span>
            <span v-if="form.tax_enabled && stageTaxPreview.taxAmount > 0" class="workspace-badge">{{ form.tax_label || 'IVA' }} ${{ formatMoney(stageTaxPreview.taxAmount) }}</span>
          </div>

          <div class="stage-modal__grid stage-modal__grid--base">
            <label class="workspace-field stage-modal__field--hero">
              <span>Nombre</span>
              <input v-model="form.name" class="workspace-input" type="text" placeholder="General Early Bird" required />
            </label>

            <label class="workspace-field">
              <span>Precio</span>
              <input v-model.number="form.price" class="workspace-input" type="number" min="0" step="0.01" required />
              <small class="workspace-field__note">{{ form.tax_enabled && !form.tax_price_includes_tax ? 'Precio base antes de impuesto.' : 'Precio que verá el comprador.' }}</small>
            </label>

            <label class="workspace-field">
              <span>Stock</span>
              <input v-model.number="form.initial_quantity" class="workspace-input" type="number" min="1" required />
            </label>

            <label class="workspace-field">
              <span>Máximo por compra</span>
              <input v-model.number="form.max_quantity" class="workspace-input" type="number" min="1" required />
            </label>

            <label class="workspace-field workspace-field--wide">
              <span>Descripción</span>
              <textarea v-model="form.description" class="workspace-input workspace-input--textarea" rows="4" placeholder="Qué incluye, para quién aplica y por qué conviene comprar esta etapa."></textarea>
            </label>

            <div class="stage-tax-card workspace-field--wide">
              <div class="stage-tax-card__head">
                <label class="workspace-inline-check workspace-inline-check--single">
                  <input v-model="form.tax_enabled" type="checkbox" />
                  <span>Agregar IVA / VAT / Sales tax</span>
                </label>
                <span v-if="form.tax_enabled" class="workspace-badge workspace-badge--mint">Final ${{ formatMoney(stageTaxPreview.grossAmount) }}</span>
              </div>
              <p>Opcional por etapa. Si está activo, el fee FOMO se calcula sobre el precio final con impuesto.</p>

              <div v-if="form.tax_enabled" class="stage-tax-card__grid">
                <label class="workspace-field">
                  <span>Nombre del impuesto</span>
                  <input v-model="form.tax_label" class="workspace-input" type="text" maxlength="40" placeholder="IVA" />
                </label>

                <label class="workspace-field">
                  <span>% impuesto</span>
                  <input v-model.number="form.tax_percent" class="workspace-input" type="number" min="0" step="0.01" placeholder="15" />
                </label>

                <label class="workspace-inline-check workspace-inline-check--single stage-tax-card__included">
                  <input v-model="form.tax_price_includes_tax" type="checkbox" />
                  <span>El precio ya incluye este impuesto</span>
                </label>
              </div>

              <div v-if="form.tax_enabled" class="stage-tax-preview">
                <span>Base ${{ formatMoney(stageTaxPreview.baseAmount) }}</span>
                <span>{{ form.tax_label || 'IVA' }} ${{ formatMoney(stageTaxPreview.taxAmount) }}</span>
                <strong>Total ${{ formatMoney(stageTaxPreview.grossAmount) }}</strong>
              </div>
            </div>
          </div>
        </section>

        <section class="stage-modal__section">
          <div class="stage-modal__section-head">
            <div>
              <span class="workspace-kicker">Checkout y codigos</span>
              <h3>Visibilidad y acceso</h3>
            </div>
          </div>

          <div class="stage-inline-group">
            <span class="stage-inline-group__label">¿Visible en checkout?</span>
            <div class="stage-toggle-pills" role="tablist" aria-label="Visibilidad en checkout">
              <button type="button" :class="['stage-toggle-pill', { 'is-active': stageCheckoutVisibility === 'checkout' }]" @click="stageCheckoutVisibility = 'checkout'">Sí</button>
              <button type="button" :class="['stage-toggle-pill', { 'is-active': stageCheckoutVisibility === 'hidden' }]" @click="stageCheckoutVisibility = 'hidden'">No</button>
            </div>
            <p class="stage-inline-helper">Si eliges <strong>Sí</strong>, la etapa aparece en la compra general del evento. Si eliges <strong>No</strong>, queda fuera del checkout y solo se abre por link privado o por códigos, según lo que definas abajo.</p>
          </div>

          <div class="stage-scope-shell">
            <div v-if="showStageCodeApplicability" class="stage-inline-group">
              <span class="stage-inline-group__label">¿A qué quieres aplicar la etapa?</span>
              <div class="stage-scope-picker" role="tablist" aria-label="Disponibilidad con codigos">
                <button type="button" :class="['stage-scope-pill', { 'is-active': stageCodeAvailability === 'all' }]" @click="stageCodeAvailability = 'all'">A todos los códigos</button>
                <button type="button" :class="['stage-scope-pill', { 'is-active': stageCodeAvailability === 'selected' }]" @click="stageCodeAvailability = 'selected'">Solo a ciertos códigos</button>
                <button type="button" :class="['stage-scope-pill', { 'is-active': stageCodeAvailability === 'none' }]" @click="stageCodeAvailability = 'none'">A ningún código</button>
              </div>
            </div>
            <p v-if="!showStageCodeApplicability && stageCheckoutVisibility === 'hidden'" class="stage-availability-note">Al guardar se generará un link privado interno para esta etapa.</p>
            <p v-else-if="showStageCodeApplicability && stageCheckoutVisibility === 'hidden' && stageCodeAvailability === 'none'" class="stage-availability-note">Se generará un link privado interno al guardar. Ese acceso no se mezcla con tus códigos del evento.</p>
            <p v-else-if="showStageCodeApplicability" class="stage-availability-note">{{ stageVisibilityNarrative }}</p>
          </div>

          <div v-if="stageCodeAvailability === 'selected'" class="stage-code-shell">
            <div class="stage-code-shell__head">
              <div>
                <strong>Códigos elegidos</strong>
                <p>Selecciona solo los códigos que deben abrir esta etapa.</p>
              </div>
              <span class="workspace-badge">{{ selectedCodeDetails.length }} seleccionados</span>
            </div>

            <label class="workspace-field workspace-field--wide">
              <span>Buscar código</span>
              <input v-model.trim="stageCodeSearch" class="workspace-input" type="text" placeholder="VIP, influencer, acceso staff..." />
            </label>

            <div v-if="selectedCodeDetails.length" class="stage-code-chip-row">
              <button v-for="eventCode in selectedCodeDetails" :key="`selected-code-${eventCode.id}`" class="stage-code-chip" type="button" @click="toggleStageCodeSelection(eventCode.id)">
                <span>{{ eventCode.code }}</span>
                <i class="fas fa-times"></i>
              </button>
            </div>

            <div v-if="!filteredActiveCodes.length" class="workspace-empty workspace-empty--compact">No encontramos códigos activos con esa búsqueda.</div>
            <div v-else class="stage-code-option-grid">
              <button
                v-for="eventCode in filteredActiveCodes"
                :key="`code-option-${eventCode.id}`"
                type="button"
                :class="['stage-code-option', { 'is-active': isSelectedStageCode(eventCode.id) }]"
                @click="toggleStageCodeSelection(eventCode.id)"
              >
                <strong>{{ eventCode.code }}</strong>
                <small>{{ eventCode.label || 'Sin etiqueta' }}</small>
              </button>
            </div>
          </div>

          <div class="stage-activation-row">
            <label class="workspace-inline-check workspace-inline-check--single">
              <input v-model="form.status" type="checkbox" />
              <span>Etapa activa</span>
            </label>
          </div>

        </section>

        <section v-if="dynamicVenueEditorAvailable" class="stage-modal__section">
          <div class="stage-modal__section-head">
            <div>
              <span class="workspace-kicker">Mapa dinámico</span>
              <h3>Conectar esta etapa a una zona</h3>
            </div>
          </div>

          <div class="stage-inline-group">
            <span class="stage-inline-group__label">Compra visual</span>
            <div class="stage-scope-picker" role="tablist" aria-label="Modo de mapa dinámico">
              <button type="button" :class="['stage-scope-pill', { 'is-active': form.dynamic_venue_mode === 'none' }]" @click="setDynamicVenueMode('none')">Sin mapa</button>
              <button type="button" :class="['stage-scope-pill', { 'is-active': form.dynamic_venue_mode === 'zone' }]" @click="setDynamicVenueMode('zone')">Zona sin asiento</button>
              <button type="button" :class="['stage-scope-pill', { 'is-active': form.dynamic_venue_mode === 'seat' }]" @click="setDynamicVenueMode('seat')">Zona con asiento</button>
            </div>
            <p class="stage-inline-helper">{{ dynamicVenueModeHelper }}</p>
          </div>

          <HostDynamicVenueMiniMap
            v-if="form.dynamic_venue_mode !== 'none'"
            v-model="form.dynamic_venue_section_ids"
            :sections="dynamicVenueSections"
            :view-box="dynamicVenueViewBox"
            :mode="form.dynamic_venue_mode"
          />
        </section>

        <section class="stage-modal__section stage-modal__section--optional">
          <button class="stage-optional-toggle" type="button" @click="advancedOpen = !advancedOpen">
            <span>
              <strong>Opcional</strong>
              <small>Límites, combos, fechas de disponibilidad y campos adicionales</small>
            </span>
            <i :class="advancedOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
          </button>

          <div v-if="advancedOpen" class="stage-optional-stack">
            <article class="stage-optional-card stage-optional-card--media">
              <div class="stage-modal__section-head stage-modal__section-head--compact">
                <div>
                  <span class="workspace-kicker">Imagen</span>
                  <h3>Imagen de la etapa</h3>
                </div>
                <p>Opcional. Se verá como miniatura en checkout y en la tarjeta de compra de esta etapa.</p>
              </div>

              <div class="stage-image-row">
                <div v-if="stageImagePreview" class="stage-image-preview">
                  <img :src="stageImagePreview" alt="Preview de la etapa" />
                </div>

                <div class="stage-image-controls">
                  <input ref="stageImageInput" class="stage-image-input" type="file" accept="image/*" @change="onStageImageSelected" />
                  <div class="stage-image-actions">
                    <button class="workspace-chip-button" type="button" @click="$refs.stageImageInput && $refs.stageImageInput.click()">
                      <i class="fas fa-image"></i>
                      <span>{{ stageImagePreview ? 'Cambiar imagen' : 'Agregar imagen' }}</span>
                    </button>
                    <span class="stage-image-helper">Usa una imagen liviana, clara y fácil de reconocer.</span>
                  </div>
                </div>
              </div>
            </article>

            <article class="stage-optional-card">
              <div class="stage-modal__section-head stage-modal__section-head--compact">
                <div>
                  <span class="workspace-kicker">Reglas comerciales</span>
                  <h3>Combos, fechas y límites</h3>
                </div>
                <p>Configura reglas y tiempos solo si hacen falta.</p>
              </div>

              <div class="stage-modal__grid stage-modal__grid--rules">
                <label class="workspace-field">
                  <span>Entradas por unidad</span>
                  <input v-model.number="form.quantity" class="workspace-input" type="number" min="1" required />
                </label>

                <label class="workspace-field">
                  <span>Categoría</span>
                  <select v-model.number="form.category" class="workspace-input workspace-select">
                    <option :value="1">FB / IG</option>
                    <option :value="2">Tiktok</option>
                    <option :value="3">Mail</option>
                    <option :value="4">Ambassador</option>
                    <option :value="5">Page</option>
                  </select>
                </label>

                <label class="workspace-field">
                  <span>Disponible desde</span>
                  <input v-model="form.sales_date" class="workspace-input" type="datetime-local" />
                </label>

                <label class="workspace-field">
                  <span>Disponible hasta</span>
                  <input v-model="form.expiration_date" class="workspace-input" type="datetime-local" />
                </label>

                <div class="workspace-field workspace-field--wide workspace-limit-card">
                  <div class="workspace-limit-card__header">
                    <div>
                      <span class="workspace-limit-card__title">Limite por codigo en esta etapa</span>
                      <small class="workspace-field__note">Decide cuántas veces puede entrar el mismo código a esta etapa privada.</small>
                    </div>
                    <label class="workspace-inline-check workspace-inline-check--compact">
                      <input v-model="codeLimitEnabled" type="checkbox" :disabled="!codeUsageLimitApplies" />
                      <span>Limitar usos</span>
                    </label>
                  </div>

                  <div class="workspace-limit-card__body">
                    <label class="workspace-field workspace-field--limit-inline">
                        <span>Máximo de usos por código</span>
                        <input
                          v-model.number="form.max_code_uses"
                          class="workspace-input"
                        type="number"
                        min="1"
                        :disabled="!codeUsageLimitApplies || !codeLimitEnabled"
                        placeholder="Ej: 3 usos"
                      />
                    </label>
                    <p class="workspace-summary-line workspace-summary-line--compact">{{ codeLimitSummary }}</p>
                  </div>
                </div>
              </div>

              <p class="workspace-summary-line">{{ comboSummary }}</p>
            </article>

            <article class="stage-optional-card stage-optional-card--fields">
              <button class="workspace-chip-button workspace-chip-button--toggle" type="button" @click="stageGuestFieldsOpen = !stageGuestFieldsOpen">
                <i :class="stageGuestFieldsOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                <span>Campos extra · {{ stageGuestFieldsSummary }}</span>
              </button>

              <div v-if="stageGuestFieldsOpen" class="workspace-list-stack">
                <div class="workspace-subpanel__head workspace-subpanel__head--tight">
                  <div>
                    <strong>Configuracion de esta etapa</strong>
                    <p>Agrega, ordena y marca los campos que se mostraran en checkout.</p>
                  </div>
                  <button class="workspace-chip-button" type="button" @click="openCatalogFieldEditor('stage')">
                    <i class="fas fa-plus"></i>
                    <span>Crear reutilizable</span>
                  </button>
                </div>

                <div v-if="catalogEditorOpen && catalogEditorContext === 'stage'" class="workspace-card workspace-card--soft">
                  <div class="workspace-subpanel__head workspace-subpanel__head--tight">
                    <div>
                      <strong>{{ editingCatalogFieldId ? 'Editar campo reutilizable' : 'Nuevo campo reutilizable' }}</strong>
                      <p>Se crea para el evento y queda agregado de una vez a esta etapa.</p>
                    </div>
                    <button class="workspace-chip-button" type="button" @click="closeCatalogFieldEditor">
                      <i class="fas fa-times"></i>
                      <span>Cerrar</span>
                    </button>
                  </div>

                  <form class="workspace-form" @submit.prevent="submitCatalogField">
                    <label class="workspace-field">
                      <span>Etiqueta</span>
                      <input v-model="catalogForm.label" class="workspace-input" type="text" placeholder="Documento" required />
                    </label>

                    <label class="workspace-field">
                      <span>Tipo</span>
                      <select v-model="catalogForm.field_type" class="workspace-input workspace-select">
                        <option value="field">Campo libre</option>
                        <option value="multiple">Seleccion unica</option>
                      </select>
                    </label>

                    <label class="workspace-field workspace-field--wide">
                      <span>Ayuda</span>
                      <input v-model="catalogForm.help_text" class="workspace-input" type="text" placeholder="Se vera debajo del campo en checkout" />
                    </label>

                    <label v-if="catalogForm.field_type === 'multiple'" class="workspace-field workspace-field--wide">
                      <span>Opciones</span>
                      <textarea v-model="catalogOptionsDraft" class="workspace-input workspace-input--textarea" rows="3" placeholder="S&#10;M&#10;L"></textarea>
                      <small class="workspace-field__note">Una opcion por linea.</small>
                    </label>

                    <label class="workspace-inline-check workspace-inline-check--single">
                      <input v-model="catalogForm.is_active" type="checkbox" />
                      <span>Campo activo</span>
                    </label>

                    <div class="workspace-actions">
                      <button class="workspace-button workspace-button--primary" type="submit" :disabled="catalogSaving">
                        <i class="fas fa-save"></i>
                        <span>{{ catalogSaving ? 'Guardando...' : editingCatalogFieldId ? 'Guardar campo' : 'Crear campo' }}</span>
                      </button>
                    </div>
                  </form>
                </div>

                <div v-if="stageAssignmentDetails.length" class="workspace-list-stack">
                  <article v-for="(assignment, index) in stageAssignmentDetails" :key="`stage-${assignment.field_id}`" class="workspace-assignment-card">
                    <div>
                      <strong>{{ assignment.label }}</strong>
                      <p>{{ fieldTypeLabel(assignment.field_type) }}</p>
                    </div>
                    <div class="workspace-assignment-controls">
                      <label class="workspace-inline-check workspace-inline-check--single">
                        <input :checked="assignment.is_required" type="checkbox" @change="toggleAssignmentRequired(stageAssignments, assignment.field_id)" />
                        <span>Obligatorio</span>
                      </label>
                      <div class="workspace-inline-actions">
                        <button class="workspace-chip-button" type="button" :disabled="index === 0" @click="moveAssignment(stageAssignments, index, -1)">
                          <i class="fas fa-arrow-up"></i>
                        </button>
                        <button class="workspace-chip-button" type="button" :disabled="index === stageAssignmentDetails.length - 1" @click="moveAssignment(stageAssignments, index, 1)">
                          <i class="fas fa-arrow-down"></i>
                        </button>
                        <button class="workspace-chip-button" type="button" @click="toggleStageAssignment(assignment.field_id)">
                          <i class="fas fa-minus"></i>
                          <span>Quitar</span>
                        </button>
                      </div>
                    </div>
                  </article>
                </div>
                <div v-else class="workspace-empty workspace-empty--compact">Esta etapa todavia no pide datos extra.</div>

                <div class="workspace-card workspace-card--soft">
                  <div class="workspace-subpanel__head workspace-subpanel__head--tight">
                    <div>
                      <strong>Catalogo disponible</strong>
                      <p>Reutiliza campos existentes del evento en esta etapa.</p>
                    </div>
                  </div>
                  <div v-if="!catalogFields.length" class="workspace-empty workspace-empty--compact">No hay campos en el catalogo aun.</div>
                  <div v-else class="workspace-list-stack">
                    <article v-for="field in catalogFields" :key="`stage-catalog-${field.id}`" class="workspace-assignment-card workspace-assignment-card--catalog">
                      <div>
                        <strong>{{ field.label }}</strong>
                        <p>{{ fieldTypeLabel(field.field_type) }}</p>
                      </div>
                      <div class="workspace-inline-actions">
                        <button class="workspace-chip-button" type="button" @click="toggleStageAssignment(field.id)">
                          <i :class="isFieldSelected(stageAssignments, field.id) ? 'fas fa-minus' : 'fas fa-plus'"></i>
                          <span>{{ isFieldSelected(stageAssignments, field.id) ? 'Quitar' : 'Agregar' }}</span>
                        </button>
                        <button class="workspace-chip-button" type="button" @click="openCatalogFieldEditor('stage', field)">
                          <i class="fas fa-pen"></i>
                          <span>Editar</span>
                        </button>
                        <button class="workspace-chip-button" type="button" @click="deleteCatalogField(field)">
                          <i class="fas fa-trash-alt"></i>
                          <span>Eliminar</span>
                        </button>
                      </div>
                    </article>
                  </div>
                </div>

                <div class="workspace-card workspace-card--soft">
                  <div class="workspace-subpanel__head workspace-subpanel__head--tight">
                    <div>
                      <strong>Preview checkout</strong>
                      <p>Se guarda despues de guardar la etapa.</p>
                    </div>
                  </div>
                  <div class="workspace-preview-stack">
                    <template v-if="stageAssignmentDetails.length">
                      <div v-for="field in stageAssignmentDetails" :key="`stage-preview-${field.field_id}`" class="workspace-preview-field">
                        <label>
                          {{ field.label }}
                          <span v-if="field.is_required">*</span>
                        </label>
                        <input v-if="field.field_type === 'field'" class="workspace-input" type="text" :placeholder="field.help_text || 'Respuesta del invitado'" disabled />
                        <select v-else class="workspace-input workspace-select" disabled>
                          <option value="">Selecciona una opcion</option>
                          <option v-for="option in field.options" :key="`${field.field_id}-preview-${option}`" :value="option">{{ option }}</option>
                        </select>
                        <small v-if="field.help_text" class="workspace-field__note">{{ field.help_text }}</small>
                      </div>
                    </template>
                    <div v-else class="workspace-empty workspace-empty--compact">Agrega campos para ver como quedara el checkout.</div>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>

        <div class="stage-form-actions">
          <button class="workspace-button workspace-button--small workspace-button--ghost" type="button" @click="resetForm">
            <i class="fas fa-rotate-left"></i>
            <span>{{ editingTicketId ? 'Cancelar edición' : 'Cancelar' }}</span>
          </button>
          <button class="workspace-button workspace-button--small workspace-button--primary" type="submit" :disabled="saving">
            <i class="fas fa-save"></i>
            <span>{{ saving ? 'Guardando...' : editingTicketId ? 'Actualizar etapa' : 'Crear etapa' }}</span>
          </button>
        </div>
      </form>
    </WorkspaceWizardModal>

    <p v-if="successMessage" class="workspace-feedback workspace-feedback--success">{{ successMessage }}</p>
    <p v-if="errorMessage" class="workspace-feedback workspace-feedback--error">{{ errorMessage }}</p>

    <article class="glass-surface workspace-panel">
      <div class="workspace-panel__header">
        <div>
          <span class="workspace-kicker">Inventario</span>
          <h2 class="workspace-title">Etapas actuales</h2>
        </div>
      </div>

      <div v-if="loading && !stageTickets.length" class="workspace-empty">Cargando etapas...</div>
      <div v-else-if="!stageTickets.length" class="workspace-empty">Todavia no hay etapas creadas. Crea la primera para empezar a vender o segmentar accesos.</div>
      <div v-else class="workspace-table-shell stages-table-shell">
        <table class="workspace-table stages-table">
          <thead>
            <tr>
              <th>Etapa</th>
              <th>Precio</th>
              <th>Stock</th>
              <th>Max</th>
              <th>Combo</th>
              <th>Checkout</th>
              <th>Acceso</th>
              <th>Link</th>
              <th>Limite por codigo</th>
              <th>Campos</th>
              <th>Status</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ticket in stageTickets" :key="ticket.id">
              <td class="stage-summary-cell" data-label="Etapa">
                <div class="workspace-table-title">{{ ticket.name }}</div>
                <p class="workspace-table-subcopy">{{ ticket.description || 'Sin descripcion.' }}</p>
              </td>
              <td data-label="Precio">
                ${{ formatMoney(stageTicketPricing(ticket).grossAmount) }}
                <small v-if="stageTicketPricing(ticket).taxAmount > 0" class="workspace-table-subcopy">{{ ticket.tax_label || 'IVA' }} incluido</small>
              </td>
              <td data-label="Stock">{{ ticket.initial_quantity }}</td>
              <td data-label="Max">{{ ticket.max_quantity }}</td>
              <td data-label="Combo">{{ comboLabel(ticket) }}</td>
              <td data-label="Checkout">{{ stageVisibilityLabel(ticket) }}</td>
              <td data-label="Acceso">{{ stageCodeAccessLabel(ticket) }}</td>
              <td class="stage-link-cell" data-label="Link">
                <a v-if="stageLink(ticket)" class="workspace-inline-link" :href="stageLink(ticket)" target="_blank" rel="noreferrer">{{ ticket.web_experience ? 'Ver evento' : 'Ver etapa' }}</a>
                <span v-else class="workspace-table-subcopy">Sin link</span>
              </td>
              <td data-label="Limite por codigo">{{ codeLimitLabel(ticket) }}</td>
              <td data-label="Campos">{{ guestFieldsCountLabel(ticket) }}</td>
              <td class="stage-status-cell" data-label="Status">
                <span :class="['workspace-status-pill', ticket.status ? 'is-active' : 'is-off']">{{ ticket.status ? 'Activa' : 'Oculta' }}</span>
              </td>
              <td class="stage-actions-cell" data-label="Acciones">
                <div class="workspace-inline-actions stage-table-actions">
                  <button class="workspace-chip-button" type="button" @click="editStage(ticket)">
                    <i class="fas fa-pen"></i>
                    <span>Editar</span>
                  </button>
                  <button class="workspace-chip-button" type="button" @click="editStage(ticket, { openGuestFields: true })">
                    <i class="fas fa-table-list"></i>
                    <span>Campos</span>
                  </button>
                  <button v-if="stageLink(ticket)" class="workspace-chip-button" type="button" @click="copyStageLink(ticket)">
                    <i class="fas fa-link"></i>
                    <span>{{ ticket.web_experience ? 'Copiar evento' : 'Copiar link' }}</span>
                  </button>
                  <button class="workspace-chip-button" type="button" @click="deleteStage(ticket)">
                    <i class="fas fa-trash-alt"></i>
                    <span>{{ ticket.status ? 'Archivar' : 'Eliminar' }}</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </article>
  </section>
</template>

<script>
import { apiService, apiServiceForm } from '../../common/api.services.js'
import { normalizeTicketTaxPercent, ticketUnitPricing } from '../../common/ticketTaxes.js'
import HostDynamicVenueMiniMap from '../../components/host/HostDynamicVenueMiniMap.vue'
import WorkspaceWizardModal from '../../components/workspace/WorkspaceWizardModal.vue'

const defaultForm = () => ({
  name: '',
  description: '',
  initial_quantity: 100,
  price: 0,
  tax_enabled: false,
  tax_label: 'IVA',
  tax_percent: 15,
  tax_price_includes_tax: false,
  status: true,
  web_experience: true,
  is_courtesy: false,
  type: 1,
  category: 5,
  sales_date: '',
  expiration_date: '',
  require_ig: false,
  quantity: 1,
  max_quantity: 5,
  max_code_uses: null,
  code: '',
  code_mode: 0,
  access_code_ids: [],
  dynamic_venue_mode: 'none',
  dynamic_venue_section_ids: [],
})

const defaultCatalogForm = () => ({
  label: '',
  field_type: 'field',
  help_text: '',
  is_active: true,
})

export default {
  name: 'StagesView',

  components: {
    HostDynamicVenueMiniMap,
    WorkspaceWizardModal,
  },

  props: {
    event: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      saving: false,
      catalogSaving: false,
      applyingGlobalAssignments: false,
      tickets: [],
      codes: [],
      catalogFields: [],
      dynamicVenueLayout: null,
      editingTicketId: null,
      editingCatalogFieldId: null,
      codeLimitEnabled: false,
      successMessage: '',
      errorMessage: '',
      editorOpen: false,
      advancedOpen: false,
      stageGuestFieldsOpen: false,
      catalogWorkspaceOpen: false,
      catalogEditorOpen: false,
      catalogEditorContext: 'global',
      catalogOptionsDraft: '',
      stageCodeSearch: '',
      stageImageFile: null,
      stageImagePreview: '',
      form: defaultForm(),
      catalogForm: defaultCatalogForm(),
      stageAssignments: [],
      globalAssignments: [],
    }
  },

  computed: {
    stageTickets() {
      return this.tickets.filter((ticket) => !ticket.is_courtesy)
    },

    activeStages() {
      return this.stageTickets.filter((ticket) => ticket.status)
    },

    hiddenStages() {
      return this.stageTickets.filter((ticket) => !ticket.status)
    },

    isCourtesyPrice() {
      return Number(this.form.price) === 0
    },

    stageTaxPreview() {
      return ticketUnitPricing(this.form)
    },

    showStageCodeApplicability() {
      return this.activeCodes.length > 0
    },

    activeCodes() {
      return this.codes.filter((code) => code.is_active)
    },

    dynamicVenueEnabled() {
      return Boolean(this.event && this.event.dynamic_venue_enabled && this.event.dynamic_venue_status === 'published')
    },

    dynamicVenueEditorAvailable() {
      return Boolean(this.dynamicVenueEnabled && this.dynamicVenueLayout && this.dynamicVenueSections.length)
    },

    dynamicVenueSections() {
      return this.dynamicVenueLayout && Array.isArray(this.dynamicVenueLayout.sections) ? this.dynamicVenueLayout.sections : []
    },

    dynamicVenueViewBox() {
      return this.dynamicVenueLayout ? this.dynamicVenueLayout.view_box : null
    },

    dynamicVenueModeHelper() {
      if (this.form.dynamic_venue_mode === 'seat') return 'El comprador deberá elegir asiento exacto en la zona seleccionada.'
      if (this.form.dynamic_venue_mode === 'zone') return 'El comprador verá la zona en el mapa y elegirá cantidad, sin asiento exacto.'
      return 'Esta etapa queda fuera del mapa y se vende con el flujo normal.'
    },

    comboSummary() {
      const units = Number(this.form.quantity) || 1
      const max = Number(this.form.max_quantity) || 1
      const total = units * max
      return units > 1
        ? `Cada unidad entrega ${units} entradas. El maximo por compra permite hasta ${total} accesos en una sola orden.`
        : `Cada unidad entrega una entrada. El maximo por compra permite hasta ${max} accesos en una sola orden.`
    },

    codeUsageLimitApplies() {
      return !this.form.web_experience && (this.stageCodeAvailability === 'all' || this.stageCodeAvailability === 'selected')
    },

    codeLimitSummary() {
      if (!this.codeUsageLimitApplies) {
        return 'No aplica aqui: esta etapa esta visible en home o no depende de codigos.'
      }

      if (!this.codeLimitEnabled || !this.form.max_code_uses) {
        return 'Cada codigo puede entrar sin limite a esta etapa privada.'
      }

      return `Un mismo codigo podra entrar hasta ${this.form.max_code_uses} veces a esta etapa.`
    },

    stageAssignmentDetails() {
      return this.enrichAssignments(this.stageAssignments)
    },

    globalAssignmentDetails() {
      return this.enrichAssignments(this.globalAssignments)
    },

    stageGuestFieldsSummary() {
      const count = this.stageAssignmentDetails.length
      if (!count) {
        return 'Sin campos'
      }
      return `${count} configurado${count === 1 ? '' : 's'}`
    },

    stageCheckoutVisibility: {
      get() {
        return this.form.web_experience ? 'checkout' : 'hidden'
      },
      set(value) {
        this.form.web_experience = value === 'checkout'
      },
    },

    stageCodeAvailability: {
      get() {
        if (!this.activeCodes.length) {
          return 'none'
        }

        if (Number(this.form.code_mode) === 1) {
          return 'all'
        }

        if (Number(this.form.code_mode) === 2) {
          if (Array.isArray(this.form.access_code_ids) && this.form.access_code_ids.length) {
            return 'selected'
          }

          if (this.autoPrivateLinkActive) {
            return 'none'
          }

          return 'selected'
        }

        return 'none'
      },
      set(value) {
        if (value === 'all') {
          this.form.code_mode = 1
          this.form.access_code_ids = []
          this.form.code = ''
          return
        }

        if (value === 'selected') {
          this.form.code_mode = 2
          this.form.code = ''
          return
        }

        this.form.code_mode = 0
        this.form.access_code_ids = []
        this.form.code = ''
        this.codeLimitEnabled = false
      },
    },

    autoPrivateLinkActive() {
      return !this.form.web_experience
        && Number(this.form.code_mode) === 2
        && (!Array.isArray(this.form.access_code_ids) || !this.form.access_code_ids.length)
        && Boolean(this.form.code)
        && String(this.form.code).trim().toUpperCase().startsWith('STAGE-')
    },

    filteredActiveCodes() {
      const term = String(this.stageCodeSearch || '').trim().toLowerCase()
      if (!term) {
        return this.activeCodes
      }

      return this.activeCodes.filter((code) => {
        const haystack = `${code.code || ''} ${code.label || ''}`.toLowerCase()
        return haystack.includes(term)
      })
    },

    selectedCodeDetails() {
      return (Array.isArray(this.form.access_code_ids) ? this.form.access_code_ids : [])
        .map((codeId) => this.activeCodes.find((code) => Number(code.id) === Number(codeId)))
        .filter(Boolean)
    },

    optionalRuleCount() {
      let count = 0

      if ((Number(this.form.quantity) || 1) > 1) count += 1
      if ((Number(this.form.type) || 1) !== 1) count += 1
      if ((Number(this.form.category) || 5) !== 5) count += 1
      if (this.form.sales_date) count += 1
      if (this.form.expiration_date) count += 1
      if (this.codeLimitEnabled && this.form.max_code_uses) count += 1

      return count
    },

    stageOptionalSummary() {
      const facts = []
      if (this.optionalRuleCount) {
        facts.push(`${this.optionalRuleCount} regla${this.optionalRuleCount === 1 ? '' : 's'}`)
      }
      if (this.stageAssignmentDetails.length) {
        const count = this.stageAssignmentDetails.length
        facts.push(`${count} campo${count === 1 ? '' : 's'}`)
      }

      return facts.length ? facts.join(' · ') : 'Sin extras activos'
    },

    stageVisibilityNarrative() {
      if (this.stageCheckoutVisibility === 'checkout') {
        if (this.stageCodeAvailability === 'selected') {
          const count = this.selectedCodeDetails.length
          return count
            ? `Visible en checkout. También acepta ${count} código${count === 1 ? '' : 's'} elegido${count === 1 ? '' : 's'}.`
            : 'Visible en checkout. Falta elegir los códigos.'
        }

        if (this.stageCodeAvailability === 'all') {
          return 'Visible en checkout. También responde a cualquier código activo.'
        }

        return 'Visible en checkout.'
      }

      if (this.stageCodeAvailability === 'none') {
        return 'Oculta en checkout. Al guardar se crea un link privado interno para abrirla desde la tabla.'
      }

      if (this.stageCodeAvailability === 'selected') {
        const count = this.selectedCodeDetails.length
        return count
          ? `Oculta en checkout. Solo entra con ${count} código${count === 1 ? '' : 's'} elegido${count === 1 ? '' : 's'}.`
          : 'Oculta en checkout. Falta elegir los códigos.'
      }

      if (this.stageCodeAvailability === 'all') {
        return 'Oculta en checkout. Entra con cualquier código activo.'
      }

      return 'Oculta en checkout.'
    },
  },

  created() {
    this.fetchAll()
  },

  watch: {
    event: {
      deep: true,
      handler() {
        this.fetchAll()
      },
    },

    codeUsageLimitApplies(value) {
      if (!value) {
        this.codeLimitEnabled = false
        this.form.max_code_uses = null
      }
    },

    codeLimitEnabled(value) {
      if (!value) {
        this.form.max_code_uses = null
      }
    },
  },

  methods: {
    onStageImageSelected(event) {
      const file = event && event.target && event.target.files && event.target.files[0]
      if (!file) {
        return
      }

      if (!String(file.type || '').startsWith('image/')) {
        this.resetMessages()
        this.errorMessage = 'La imagen de la etapa debe ser un archivo válido.'
        if (this.$refs.stageImageInput) {
          this.$refs.stageImageInput.value = ''
        }
        return
      }

      this.stageImageFile = file
      this.stageImagePreview = window.URL.createObjectURL(file)
    },

    uploadStageImage(ticketId) {
      if (!this.stageImageFile) {
        return Promise.resolve({ ok: true })
      }

      const payload = new FormData()
      payload.append('generic_image', this.stageImageFile)

      return apiServiceForm(`/api/host/events/tickets/${ticketId}/`, 'PATCH', payload)
        .then((response) => {
          if (!response || response.err) {
            return { ok: false, err: (response && response.err) || 'La etapa se guardó, pero no pudimos subir su imagen.' }
          }
          return { ok: true, data: response }
        })
    },

    generateStagePrivateCode() {
      const current = String(this.form.code || '').trim().toUpperCase()
      if (current && current.startsWith('STAGE-')) {
        return current
      }

      const base = String(this.form.name || 'stage')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toUpperCase()
        .slice(0, 12) || 'STAGE'

      return `STAGE-${base}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`
    },

    stagePrimaryCode(ticket) {
      if (Array.isArray(ticket.access_codes_detail) && ticket.access_codes_detail.length) {
        return ticket.access_codes_detail[0].code
      }

      return ticket.code || ''
    },

    stageLink(ticket) {
      const eventBase = `${window.location.origin}/event/${this.event.slug}`
      const base = `${eventBase}?stage=${ticket.id}`
      const code = this.stagePrimaryCode(ticket)

      if (ticket.web_experience) {
        return eventBase
      }

      if (code) {
        return `${base}&co=${encodeURIComponent(code)}&wanted=true`
      }

      if (!ticket.web_experience && Number(ticket.code_mode) === 0) {
        return base
      }

      return ''
    },

    copyStageLink(ticket) {
      const link = this.stageLink(ticket)
      this.resetMessages()

      if (!link) {
        this.errorMessage = 'Esta etapa no tiene un link directo todavia.'
        return
      }

      navigator.clipboard.writeText(link).then(() => {
        this.successMessage = `Link de ${ticket.name} copiado.`
      }).catch(() => {
        this.errorMessage = 'No se pudo copiar el link.'
      })
    },

    toggleStageCodeSelection(codeId) {
      const nextIds = Array.isArray(this.form.access_code_ids) ? [...this.form.access_code_ids] : []
      const normalizedId = Number(codeId)
      const index = nextIds.findIndex((item) => Number(item) === normalizedId)

      if (index >= 0) {
        nextIds.splice(index, 1)
      } else {
        nextIds.push(normalizedId)
      }

      this.form.access_code_ids = nextIds
      this.stageCodeAvailability = 'selected'
    },

    normalizeDynamicVenueKey(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
    },

    dynamicVenueSectionMode(section) {
      return section && (section.kind === 'zone' || section.selection_mode === 'zone') ? 'zone' : 'seat'
    },

    inferDynamicVenueBinding(ticket) {
      if (!this.dynamicVenueEnabled || !this.dynamicVenueSections.length) {
        return { mode: 'none', sectionIds: [] }
      }

      const haystack = this.normalizeDynamicVenueKey(`${ticket.name || ''} ${ticket.description || ''}`)
      const matches = this.dynamicVenueSections.filter((section) => {
        const aliases = Array.isArray(section.aliases) ? section.aliases : []
        const values = [section.id, section.name, ...aliases]
        return values.some((value) => {
          const needle = this.normalizeDynamicVenueKey(value)
          return Boolean(needle && haystack.includes(needle))
        })
      })

      if (!matches.length) {
        return { mode: 'none', sectionIds: [] }
      }

      const hasSeatSection = matches.some((section) => this.dynamicVenueSectionMode(section) === 'seat')
      return {
        mode: hasSeatSection ? 'seat' : 'zone',
        sectionIds: matches.map((section) => String(section.id || '').trim()).filter(Boolean),
      }
    },

    setDynamicVenueMode(mode) {
      if (this.form.dynamic_venue_mode === mode) return
      this.form.dynamic_venue_mode = mode
      this.form.dynamic_venue_section_ids = []
    },

    isSelectedStageCode(codeId) {
      return Array.isArray(this.form.access_code_ids)
        && this.form.access_code_ids.some((selectedId) => Number(selectedId) === Number(codeId))
    },

    stageHasOptionalContent(ticket = {}, guestFields = []) {
      return (Number(ticket.quantity) || 1) > 1
        || (Number(ticket.type) || 1) !== 1
        || (Number(ticket.category) || 5) !== 5
        || Boolean(ticket.sales_date)
        || Boolean(ticket.expiration_date)
        || Boolean(ticket.max_code_uses)
        || Boolean(ticket.generic_image)
        || (Array.isArray(guestFields) && guestFields.length > 0)
    },

    fetchAll() {
      if (!this.event || !this.event.id) {
        return
      }

      this.loading = true
      this.fetchDynamicVenueLayout()
      Promise.all([
        apiService(`/api/host/events/${this.event.id}/tickets/`),
        apiService(`/api/host/events/${this.event.id}/codes/`),
        apiService(`/api/host/events/${this.event.id}/guest-fields/`),
      ]).then(([tickets, codes, fields]) => {
        this.tickets = Array.isArray(tickets) ? tickets : []
        this.codes = Array.isArray(codes) ? codes : []
        this.catalogFields = Array.isArray(fields) ? fields : []

        this.stageAssignments = this.pruneAssignments(this.stageAssignments)
        this.globalAssignments = this.pruneAssignments(this.globalAssignments)

        if (!this.globalAssignments.length) {
          const sourceTicket = this.stageTickets.find((ticket) => Array.isArray(ticket.guest_fields) && ticket.guest_fields.length)
          if (sourceTicket) {
            this.globalAssignments = this.mapGuestFieldsToAssignments(sourceTicket.guest_fields)
          }
        }
      }).finally(() => {
        this.loading = false
      })
    },

    fetchDynamicVenueLayout() {
      if (!this.dynamicVenueEnabled || !this.event.slug) {
        this.dynamicVenueLayout = null
        return
      }

      apiService(`/api/events/${this.event.slug}/venue-map/?scope=host`).then((data) => {
        this.dynamicVenueLayout = data && data.enabled && data.layout ? data.layout : null
      }).catch(() => {
        this.dynamicVenueLayout = null
      })
    },

    resetMessages() {
      this.successMessage = ''
      this.errorMessage = ''
    },

    formatMoney(value) {
      const parsed = Number.parseFloat(value)
      return Number.isNaN(parsed) ? '0.00' : parsed.toFixed(2)
    },

    stageTicketPricing(ticket) {
      return ticketUnitPricing(ticket)
    },

    displayTaxPercent(value) {
      const percent = normalizeTicketTaxPercent(value) * 100
      return Number.isFinite(percent) ? Number(percent.toFixed(4)) : 0
    },

    formatInputDate(value) {
      if (!value) {
        return ''
      }

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return ''
      }

      const pad = (number) => `${number}`.padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
    },

    fieldTypeLabel(value) {
      return value === 'multiple' ? 'Seleccion unica' : 'Campo libre'
    },

    normalizeOptionsDraft(options) {
      return String(options || '')
        .split(/\r?\n/)
        .map((option) => option.trim())
        .filter((option, index, list) => option !== '' && list.indexOf(option) === index)
    },

    resetCatalogFieldEditor() {
      this.editingCatalogFieldId = null
      this.catalogForm = defaultCatalogForm()
      this.catalogOptionsDraft = ''
    },

    openCatalogFieldEditor(context = 'global', field = null) {
      this.catalogEditorContext = context
      this.catalogEditorOpen = true

      if (context === 'global') {
        this.catalogWorkspaceOpen = true
      }

      if (context === 'stage') {
        this.editorOpen = true
        this.advancedOpen = true
        this.stageGuestFieldsOpen = true
      }

      if (!field) {
        this.resetCatalogFieldEditor()
        return
      }

      this.editingCatalogFieldId = field.id
      this.catalogForm = {
        label: field.label || '',
        field_type: field.field_type || 'field',
        help_text: field.help_text || '',
        is_active: field.is_active !== false,
      }
      this.catalogOptionsDraft = Array.isArray(field.options) ? field.options.join('\n') : ''
    },

    closeCatalogFieldEditor() {
      this.catalogEditorOpen = false
      this.resetCatalogFieldEditor()
    },

    submitCatalogField() {
      this.resetMessages()
      this.catalogSaving = true

      const endpoint = this.editingCatalogFieldId
        ? `/api/host/events/${this.event.id}/guest-fields/${this.editingCatalogFieldId}/`
        : `/api/host/events/${this.event.id}/guest-fields/`

      const payload = {
        ...this.catalogForm,
        options: this.catalogForm.field_type === 'multiple' ? this.normalizeOptionsDraft(this.catalogOptionsDraft) : [],
      }

      apiService(endpoint, this.editingCatalogFieldId ? 'PATCH' : 'POST', payload).then((data) => {
        if (!data || data.err) {
          this.errorMessage = (data && data.err) || 'No se pudo guardar el campo reutilizable.'
          return
        }

        const createdFieldId = data.id
        this.successMessage = this.editingCatalogFieldId ? 'Campo reutilizable actualizado.' : 'Campo reutilizable creado.'

        if (!this.editingCatalogFieldId && this.catalogEditorContext === 'stage' && createdFieldId) {
          this.stageAssignments.push({ field_id: createdFieldId, is_required: false, sort_order: this.stageAssignments.length })
          this.reindexAssignments(this.stageAssignments)
        }

        if (!this.editingCatalogFieldId && this.catalogEditorContext === 'global' && createdFieldId) {
          this.globalAssignments.push({ field_id: createdFieldId, is_required: false, sort_order: this.globalAssignments.length })
          this.reindexAssignments(this.globalAssignments)
        }

        this.closeCatalogFieldEditor()
        this.fetchAll()
      }).finally(() => {
        this.catalogSaving = false
      })
    },

    deleteCatalogField(field) {
      if (!field || !window.confirm(`Eliminar ${field.label}? Se quitara del catalogo y de las etapas donde este asignado.`)) {
        return
      }

      this.resetMessages()
      apiService(`/api/host/events/${this.event.id}/guest-fields/${field.id}/`, 'DELETE').then((data) => {
        if (data && data.err) {
          this.errorMessage = data.err
          return
        }

        this.stageAssignments = this.pruneAssignments(this.stageAssignments.filter((assignment) => assignment.field_id !== field.id))
        this.globalAssignments = this.pruneAssignments(this.globalAssignments.filter((assignment) => assignment.field_id !== field.id))
        this.successMessage = 'Campo reutilizable eliminado.'
        if (this.editingCatalogFieldId === field.id) {
          this.closeCatalogFieldEditor()
        }
        this.fetchAll()
      }).catch(() => {
        this.errorMessage = 'No se pudo eliminar el campo reutilizable.'
      })
    },

    enrichAssignments(assignments) {
      return this.pruneAssignments(assignments)
        .map((assignment) => {
          const field = this.catalogFields.find((item) => item.id === assignment.field_id)
          if (!field) {
            return null
          }
          return {
            ...field,
            field_id: field.id,
            is_required: Boolean(assignment.is_required),
            sort_order: assignment.sort_order,
          }
        })
        .filter(Boolean)
        .sort((left, right) => left.sort_order - right.sort_order)
    },

    pruneAssignments(assignments) {
      const validIds = new Set(this.catalogFields.map((field) => field.id))
      const seen = new Set()
      const nextAssignments = []

      ;(Array.isArray(assignments) ? assignments : []).forEach((assignment) => {
        const fieldId = Number(assignment.field_id)
        if (!validIds.has(fieldId) || seen.has(fieldId)) {
          return
        }
        seen.add(fieldId)
        nextAssignments.push({
          field_id: fieldId,
          is_required: Boolean(assignment.is_required),
          sort_order: nextAssignments.length,
        })
      })

      return nextAssignments
    },

    reindexAssignments(assignments) {
      assignments.forEach((assignment, index) => {
        assignment.sort_order = index
      })
    },

    isFieldSelected(assignments, fieldId) {
      return assignments.some((assignment) => Number(assignment.field_id) === Number(fieldId))
    },

    toggleAssignment(listName, fieldId) {
      const target = this[listName]
      const index = target.findIndex((assignment) => Number(assignment.field_id) === Number(fieldId))
      if (index >= 0) {
        target.splice(index, 1)
        this.reindexAssignments(target)
        return
      }
      target.push({ field_id: Number(fieldId), is_required: false, sort_order: target.length })
      this.reindexAssignments(target)
    },

    toggleStageAssignment(fieldId) {
      this.toggleAssignment('stageAssignments', fieldId)
    },

    toggleGlobalAssignment(fieldId) {
      this.toggleAssignment('globalAssignments', fieldId)
    },

    toggleAssignmentRequired(assignments, fieldId) {
      const target = assignments.find((assignment) => Number(assignment.field_id) === Number(fieldId))
      if (target) {
        target.is_required = !target.is_required
      }
    },

    moveAssignment(assignments, index, direction) {
      const targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= assignments.length) {
        return
      }
      const current = assignments[index]
      assignments.splice(index, 1)
      assignments.splice(targetIndex, 0, current)
      this.reindexAssignments(assignments)
    },

    mapGuestFieldsToAssignments(fields) {
      return (Array.isArray(fields) ? fields : []).map((field, index) => ({
        field_id: Number(field.field_id || field.id),
        is_required: Boolean(field.is_required),
        sort_order: Number(field.sort_order == null ? index : field.sort_order),
      }))
    },

    buildAssignmentPayload(assignments) {
      return {
        assignments: this.pruneAssignments(assignments).map((assignment, index) => ({
          field_id: assignment.field_id,
          is_required: Boolean(assignment.is_required),
          sort_order: index,
        })),
      }
    },

    saveTicketGuestFields(ticketId, assignments) {
      return apiService(`/api/host/events/tickets/${ticketId}/guest-fields/`, 'PUT', this.buildAssignmentPayload(assignments))
    },

    toggleCatalogWorkspace() {
      this.catalogWorkspaceOpen = !this.catalogWorkspaceOpen
      if (this.catalogWorkspaceOpen && !this.globalAssignments.length) {
        const sourceTicket = this.stageTickets.find((ticket) => Array.isArray(ticket.guest_fields) && ticket.guest_fields.length)
        if (sourceTicket) {
          this.globalAssignments = this.mapGuestFieldsToAssignments(sourceTicket.guest_fields)
        }
      }
    },

    applyGlobalAssignmentsToCurrentStages() {
      const confirmationText = this.globalAssignments.length
        ? `Aplicar esta configuracion a las ${this.stageTickets.length} etapas actuales?`
        : `Quitar los campos extra de las ${this.stageTickets.length} etapas actuales y dejar solo Nombre y Apellido?`

      if (!window.confirm(confirmationText)) {
        return
      }

      this.resetMessages()
      this.applyingGlobalAssignments = true

      Promise.all(this.stageTickets.map((ticket) => this.saveTicketGuestFields(ticket.id, this.globalAssignments)))
        .then((responses) => {
          const failed = responses.find((response) => response && response.err)
          if (failed) {
            this.errorMessage = failed.err || 'No se pudo aplicar la configuracion global.'
            return
          }
          this.successMessage = 'Configuracion aplicada a todas las etapas actuales.'
          this.fetchAll()
        })
        .finally(() => {
          this.applyingGlobalAssignments = false
        })
    },

    openCreatePanel(options = {}) {
      this.resetMessages()
      this.editingTicketId = null
      this.editorOpen = true
      this.advancedOpen = false
      this.stageGuestFieldsOpen = Boolean(options.openGuestFields)
      this.stageCodeSearch = ''
      this.stageImageFile = null
      this.stageImagePreview = ''
      this.codeLimitEnabled = false
      this.form = defaultForm()
      this.stageAssignments = []
      this.closeCatalogFieldEditor()
    },

    stageVisibilityLabel(ticket) {
      return ticket.web_experience ? 'Visible' : 'Oculta'
    },

    stageCodeAccessLabel(ticket) {
      if (!ticket.web_experience && Number(ticket.code_mode) === 0) {
        return 'Link privado'
      }

      if (Number(ticket.code_mode) === 1) {
        return 'Cualquier codigo'
      }

      if (Number(ticket.code_mode) === 2) {
        if (ticket.code && (!Array.isArray(ticket.access_codes_detail) || !ticket.access_codes_detail.length)) {
          return 'Link privado'
        }
        const code = this.stagePrimaryCode(ticket)
        if (Array.isArray(ticket.access_codes_detail) && ticket.access_codes_detail.length > 1) {
          return `${ticket.access_codes_detail.length} codigos elegidos`
        }
        if (code) {
          return `Link privado · ${code}`
        }
        return 'Codigos elegidos'
      }

      return 'Sin codigo'
    },

    comboLabel(ticket) {
      const quantity = Number(ticket.quantity) || 1
      return quantity > 1 ? `${quantity}x por unidad` : 'Simple'
    },

    accessCodeSummary(ticket) {
      const count = Array.isArray(ticket.access_codes_detail) ? ticket.access_codes_detail.length : 0
      if (!count) {
        return 'Sin seleccion'
      }
      if (count === 1) {
        return ticket.access_codes_detail[0].code
      }
      return `${count} codigos`
    },

    codeLimitLabel(ticket) {
      if (ticket.web_experience || Number(ticket.code_mode) === 0) {
        return 'No aplica'
      }
      return ticket.max_code_uses ? `${ticket.max_code_uses} usos max.` : 'Sin limite'
    },

    guestFieldsCountLabel(ticket) {
      const count = Array.isArray(ticket.guest_fields) ? ticket.guest_fields.length : 0
      return count ? `${count} configurados` : 'Sin campos'
    },

    normalizedPayload() {
      const selectedCode = this.codes.find((code) => code.id === Number(this.form.access_code_ids[0]))
      const codeAvailability = this.stageCodeAvailability
      const shouldAutoGeneratePrivateCode = this.stageCheckoutVisibility === 'hidden' && codeAvailability === 'none'
      const privateCode = shouldAutoGeneratePrivateCode ? this.generateStagePrivateCode() : ''
      return {
        ...this.form,
        code_mode: codeAvailability === 'all' ? 1 : codeAvailability === 'selected' || shouldAutoGeneratePrivateCode ? 2 : 0,
        is_courtesy: this.isCourtesyPrice,
        category: Number(this.form.category),
        initial_quantity: Number(this.form.initial_quantity),
        price: Number(this.form.price),
        tax_enabled: Boolean(this.form.tax_enabled),
        tax_label: this.form.tax_label || 'IVA',
        tax_percent: this.form.tax_enabled ? normalizeTicketTaxPercent(this.form.tax_percent) : 0,
        tax_price_includes_tax: Boolean(this.form.tax_enabled && this.form.tax_price_includes_tax),
        quantity: Number(this.form.quantity),
        max_quantity: Number(this.form.max_quantity),
        max_code_uses: this.codeUsageLimitApplies && this.codeLimitEnabled && this.form.max_code_uses ? Number(this.form.max_code_uses) : null,
        web_experience: this.stageCheckoutVisibility === 'checkout',
        require_ig: false,
        sales_date: this.form.sales_date ? `${this.form.sales_date}:00` : null,
        expiration_date: this.form.expiration_date ? `${this.form.expiration_date}:00` : null,
        access_code_ids: codeAvailability === 'selected' ? this.form.access_code_ids : [],
        code: shouldAutoGeneratePrivateCode ? privateCode : codeAvailability === 'selected' && selectedCode ? selectedCode.code : '',
        type: this.isCourtesyPrice ? 3 : 1,
        dynamic_venue_mode: this.dynamicVenueEnabled ? this.form.dynamic_venue_mode : 'none',
        dynamic_venue_section_ids: this.dynamicVenueEnabled && this.form.dynamic_venue_mode !== 'none' ? this.form.dynamic_venue_section_ids : [],
      }
    },

    submitStage() {
      if (this.dynamicVenueEnabled && this.form.dynamic_venue_mode !== 'none' && !this.form.dynamic_venue_section_ids.length) {
        this.resetMessages()
        this.errorMessage = 'Elige al menos una zona del mapa dinámico para esta etapa.'
        return
      }

      if (this.stageCodeAvailability === 'selected' && !this.form.access_code_ids.length) {
        this.resetMessages()
        this.errorMessage = 'Selecciona al menos un codigo para dejar la etapa ligada a codigos elegidos.'
        return
      }

      this.resetMessages()
      this.saving = true

      const endpoint = this.editingTicketId
        ? `/api/host/events/tickets/${this.editingTicketId}/`
        : `/api/host/events/${this.event.id}/tickets/`

      apiService(endpoint, this.editingTicketId ? 'PATCH' : 'POST', this.normalizedPayload())
        .then((data) => {
          if (!data || data.err) {
            this.errorMessage = (data && data.err) || 'No se pudo guardar la etapa.'
            return null
          }
          return data
        })
        .then((ticket) => {
          if (!ticket || !ticket.id) {
            return null
          }
          return this.uploadStageImage(ticket.id)
            .then((imageResponse) => {
              if (imageResponse && imageResponse.ok === false) {
                this.errorMessage = imageResponse.err
              }

              return this.saveTicketGuestFields(ticket.id, this.stageAssignments)
            })
            .then((assignmentResponse) => {
              if (assignmentResponse && assignmentResponse.err) {
                this.errorMessage = assignmentResponse.err || 'La etapa se guardo, pero no pudimos actualizar sus campos extra.'
                return null
              }

              if (this.isCourtesyPrice) {
                this.successMessage = this.editingTicketId ? 'Guardada como cortesia y actualizada.' : 'Guardada como cortesia.'
              } else {
                this.successMessage = this.editingTicketId ? 'Etapa y campos extra actualizados.' : 'Etapa y campos extra creados.'
              }
              this.fetchAll()
              this.resetForm()
              return assignmentResponse
            })
        })
        .finally(() => {
          this.saving = false
        })
    },

    editStage(ticket, options = {}) {
      this.resetMessages()
      this.editingTicketId = ticket.id
      this.editorOpen = true
      this.codeLimitEnabled = Boolean(ticket.max_code_uses)
      this.stageAssignments = this.mapGuestFieldsToAssignments(ticket.guest_fields)
      this.stageCodeSearch = ''
      this.stageImageFile = null
      this.stageImagePreview = ticket.generic_image || ''
      this.advancedOpen = Boolean(options.openGuestFields || this.stageHasOptionalContent(ticket, this.stageAssignments))
      this.stageGuestFieldsOpen = Boolean(options.openGuestFields || this.stageAssignments.length)
      this.closeCatalogFieldEditor()
      const persistedDynamicVenueMode = ['zone', 'seat'].includes(String(ticket.dynamic_venue_mode || 'none')) ? ticket.dynamic_venue_mode : 'none'
      const persistedDynamicVenueSections = Array.isArray(ticket.dynamic_venue_section_ids)
        ? ticket.dynamic_venue_section_ids.map((sectionId) => String(sectionId || '').trim()).filter(Boolean)
        : []
      const inferredDynamicVenueBinding = persistedDynamicVenueMode !== 'none' && persistedDynamicVenueSections.length
        ? { mode: persistedDynamicVenueMode, sectionIds: persistedDynamicVenueSections }
        : this.inferDynamicVenueBinding(ticket)
      this.form = {
        name: ticket.name || '',
        description: ticket.description || '',
        initial_quantity: ticket.initial_quantity || 0,
        price: Number.parseFloat(ticket.price) || 0,
        tax_enabled: Boolean(ticket.tax_enabled),
        tax_label: ticket.tax_label || 'IVA',
        tax_percent: this.displayTaxPercent(ticket.tax_percent),
        tax_price_includes_tax: Boolean(ticket.tax_price_includes_tax),
        status: Boolean(ticket.status),
        web_experience: Boolean(ticket.web_experience),
        is_courtesy: Boolean(ticket.is_courtesy),
        type: Number(ticket.type) || 1,
        category: Number(ticket.category) || 5,
        sales_date: this.formatInputDate(ticket.sales_date),
        expiration_date: this.formatInputDate(ticket.expiration_date),
        require_ig: Boolean(ticket.require_ig),
        quantity: Number(ticket.quantity) || 1,
        max_quantity: Number(ticket.max_quantity) || 5,
        max_code_uses: Number(ticket.max_code_uses) || null,
        code: ticket.code || '',
        code_mode: Number(ticket.code_mode) || 0,
        access_code_ids: Array.isArray(ticket.access_codes_detail) ? ticket.access_codes_detail.map((code) => code.id) : [],
        dynamic_venue_mode: inferredDynamicVenueBinding.mode,
        dynamic_venue_section_ids: inferredDynamicVenueBinding.sectionIds,
      }
    },

    resetForm() {
      this.editingTicketId = null
      this.editorOpen = false
      this.advancedOpen = false
      this.stageGuestFieldsOpen = false
      this.stageCodeSearch = ''
      this.stageImageFile = null
      this.stageImagePreview = ''
      if (this.$refs.stageImageInput) {
        this.$refs.stageImageInput.value = ''
      }
      this.codeLimitEnabled = false
      this.form = defaultForm()
      this.stageAssignments = []
      this.closeCatalogFieldEditor()
    },

    deleteStage(ticket) {
      this.resetMessages()
      apiService(`/api/host/events/tickets/${ticket.id}/`, 'DELETE').then((data) => {
        if (data && data.err) {
          this.errorMessage = data.err
          return
        }
        this.successMessage = ticket.status ? 'Etapa archivada.' : 'Etapa eliminada.'
        if (this.editingTicketId === ticket.id) {
          this.resetForm()
        }
        this.fetchAll()
      }).catch(() => {
        this.errorMessage = 'No se pudo eliminar la etapa.'
      })
    },
  },
}
</script>

<style scoped>
.workspace-panel--hero,
.workspace-editor-panel,
.workspace-subpanel,
.workspace-list-stack,
.workspace-preview-stack,
.workspace-assignment-card,
.workspace-card--soft {
  display: grid;
  gap: 18px;
}

.workspace-panel__header--spread,
.workspace-subpanel__head,
.workspace-assignment-card,
.workspace-limit-card__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.workspace-subpanel__head--tight,
.workspace-assignment-card--catalog {
  gap: 12px;
}

.workspace-two-column {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

.workspace-subpanel {
  padding: 20px;
  border-radius: 20px;
  border: 1px solid rgba(223, 214, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
}

.workspace-subpanel h3,
.workspace-subpanel strong,
.workspace-table-title,
.workspace-limit-card__title,
.workspace-preview-field label {
  color: var(--text-main);
}

.workspace-subpanel h3,
.workspace-subpanel p,
.workspace-preview-field label,
.workspace-table-subcopy {
  margin: 0;
}

.workspace-card--soft,
.workspace-advanced-block,
.workspace-limit-card,
.workspace-guest-fields-block {
  padding: 16px;
  border: 1px solid rgba(223, 214, 255, 0.08);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.02);
}

.workspace-chip-button--toggle {
  justify-self: flex-start;
}

.workspace-advanced-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.workspace-summary-line {
  margin: 0;
  padding: 14px 16px;
  border-radius: 14px;
  color: var(--text-soft);
  background: rgba(203, 255, 232, 0.06);
  border: 1px solid rgba(203, 255, 232, 0.12);
  grid-column: 1 / -1;
}

.workspace-summary-line--compact {
  padding: 0;
  background: transparent;
  border: 0;
}

.workspace-field--limit-inline,
.workspace-preview-field,
.workspace-assignment-controls {
  display: grid;
  gap: 8px;
}

.workspace-assignment-card {
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid rgba(223, 214, 255, 0.08);
  background: rgba(255, 255, 255, 0.025);
}

.workspace-assignment-card p,
.workspace-empty--compact {
  margin: 0;
}

.workspace-chip-row,
.workspace-inline-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.stage-actions-cell {
  min-width: 220px;
}

.stage-table-actions {
  gap: 6px;
}

.stage-table-actions .workspace-chip-button {
  min-height: 34px;
  padding: 0 12px;
  border-radius: 10px;
  box-shadow: none;
  background: rgba(255, 255, 255, 0.035);
}

.stage-table-actions .workspace-chip-button span {
  font-size: 0.9rem;
}

.stage-modal,
.stage-optional-stack,
.stage-code-shell,
.stage-modal-summary-card,
.stage-summary-list,
.stage-modal-checklist,
.stage-summary-inline-grid {
  display: grid;
  gap: 18px;
}

.stage-modal__section {
  position: relative;
  display: grid;
  gap: 18px;
  padding: 24px;
  border-radius: 26px;
  border: 1px solid rgba(223, 214, 255, 0.08);
  background: rgba(255, 255, 255, 0.025);
  overflow: hidden;
  animation: stage-modal-rise 240ms ease both;
}

.stage-modal__section::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(188, 240, 236, 0.55), rgba(218, 232, 255, 0.42), transparent);
  pointer-events: none;
}

.stage-modal__section--optional {
  gap: 14px;
}

.stage-modal__section--summary {
  gap: 16px;
}

.stage-modal__section-head {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-start;
}

.stage-modal__section-head--compact {
  gap: 12px;
}

.stage-modal__section-head h3,
.stage-modal-summary-card h3 {
  margin: 6px 0 0;
  color: var(--text-main);
}

.stage-modal__section-head p,
.stage-modal-summary-card p {
  margin: 0;
  color: var(--text-soft);
  line-height: 1.6;
}

.stage-modal__grid {
  display: grid;
  gap: 16px;
}

.stage-modal__grid--base {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.stage-modal__grid--rules {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.stage-modal__field--hero {
  grid-column: span 2;
}

.stage-base-facts,
.stage-access-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.stage-access-fact {
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  color: var(--text-soft);
  border: 1px solid rgba(196, 206, 228, 0.12);
  background: rgba(255, 255, 255, 0.035);
}

.stage-availability-picker,
.stage-scope-picker,
.stage-toggle-grid,
.stage-code-option-grid,
.stage-modal-summary-pills,
.stage-modal-footer {
  display: grid;
  gap: 12px;
}

.stage-inline-group {
  display: grid;
  gap: 10px;
}

.stage-inline-group__label {
  color: var(--text-main);
  font-weight: 700;
  letter-spacing: -0.01em;
}

.stage-inline-helper {
  margin: 0;
  color: var(--text-soft);
  line-height: 1.55;
  max-width: 780px;
}

.stage-inline-helper strong {
  color: var(--text-main);
}

.stage-tax-card {
  display: grid;
  gap: 12px;
  padding: 16px;
  border-radius: 18px;
  border: 1px solid rgba(196, 206, 228, 0.12);
  background: rgba(255, 255, 255, 0.035);
}

.stage-tax-card__head,
.stage-tax-preview {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.stage-tax-card p {
  margin: 0;
  color: var(--text-soft);
  line-height: 1.5;
}

.stage-tax-card__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.stage-tax-card__included {
  grid-column: span 2;
}

.stage-tax-preview {
  padding-top: 10px;
  border-top: 1px solid rgba(196, 206, 228, 0.12);
  color: var(--text-soft);
}

.stage-tax-preview strong {
  color: var(--text-main);
}

.stage-toggle-pills {
  display: inline-grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.stage-toggle-pill {
  min-height: 42px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid rgba(196, 206, 228, 0.12);
  background: rgba(255, 255, 255, 0.03);
  color: var(--text-soft);
  font-weight: 700;
  text-align: center;
  transition: border-color 180ms ease, background 180ms ease, color 180ms ease;
}

.stage-toggle-pill.is-active {
  color: var(--text-main);
  border-color: rgba(196, 206, 228, 0.24);
  background: rgba(255, 255, 255, 0.08);
}

.stage-activation-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  align-items: center;
  padding-top: 2px;
}

.stage-inline-note {
  color: var(--text-soft);
  font-size: 0.9rem;
  line-height: 1.45;
}

.stage-availability-picker {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.stage-availability-picker--checkout {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.stage-scope-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.stage-toggle-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.stage-choice-card,
.stage-toggle-card,
.stage-code-option,
.stage-scope-pill,
.stage-code-chip,
.stage-optional-toggle {
  border: 1px solid rgba(196, 206, 228, 0.1);
  color: var(--text-main);
  background: rgba(255, 255, 255, 0.035);
}

.stage-choice-card,
.stage-toggle-card,
.stage-code-option {
  display: grid;
  gap: 6px;
  padding: 16px;
  border-radius: 16px;
  text-align: left;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  transition: border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;
}

.stage-choice-card.is-active,
.stage-toggle-card.is-active,
.stage-code-option.is-active {
  border-color: rgba(196, 206, 228, 0.22);
  background: rgba(255, 255, 255, 0.06);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.stage-choice-card strong,
.stage-toggle-card strong,
.stage-code-option strong {
  font-size: 1rem;
}

.stage-choice-card small,
.stage-toggle-card small,
.stage-code-option small,
.stage-summary-list span,
.stage-modal-checklist li {
  color: var(--text-soft);
  line-height: 1.55;
}

.stage-choice-card__icon,
.stage-toggle-card__icon {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid rgba(196, 206, 228, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(213, 221, 234, 0.86);
  box-shadow: none;
}

.stage-scope-shell {
  display: grid;
  gap: 12px;
}

.stage-scope-pill {
  min-height: 42px;
  padding: 0 16px;
  border-radius: 12px;
  font-weight: 700;
  transition: border-color 180ms ease, background 180ms ease, color 180ms ease;
}

.stage-scope-pill.is-active {
  color: var(--text-main);
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(196, 206, 228, 0.24);
}

.stage-availability-note {
  margin: 0;
  padding: 12px 14px;
  border-radius: 12px;
  color: var(--text-soft);
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(196, 206, 228, 0.08);
  line-height: 1.5;
}

.stage-toggle-card {
  position: relative;
  grid-template-columns: auto 1fr;
  align-items: center;
}

.stage-toggle-card--info {
  cursor: default;
}

.stage-toggle-card input {
  position: absolute;
  inset: 0;
  opacity: 0;
}

.stage-code-shell {
  padding: 18px;
  border-radius: 22px;
  border: 1px solid rgba(223, 214, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.stage-code-shell--compact {
  gap: 0;
}

.stage-code-shell__head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.stage-code-shell__head p {
  margin: 6px 0 0;
  color: var(--text-soft);
}

.stage-code-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.stage-code-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 999px;
  transition: transform 180ms ease, border-color 180ms ease, background 180ms ease;
}

.stage-code-option-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.stage-image-row,
.stage-image-controls,
.stage-image-actions {
  display: grid;
  gap: 14px;
}

.stage-image-row {
  grid-template-columns: minmax(180px, 220px) minmax(0, 1fr);
  align-items: start;
}

.stage-image-input {
  display: none;
}

.stage-image-preview {
  width: 100%;
  max-width: 220px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(196, 206, 228, 0.1);
  background: rgba(255, 255, 255, 0.02);
}

.stage-image-preview img {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
}

.stage-image-helper {
  color: var(--text-soft);
  line-height: 1.5;
}

.stage-optional-toggle {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  width: 100%;
  padding: 16px 18px;
  border-radius: 16px;
  text-align: left;
  transition: border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;
}

.stage-optional-toggle span {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.stage-optional-toggle strong,
.stage-modal-summary-card h3 {
  color: var(--text-main);
}

.stage-optional-toggle small {
  color: var(--text-soft);
  line-height: 1.5;
}

.stage-optional-card {
  display: grid;
  gap: 18px;
  padding: 20px;
  border-radius: 22px;
  border: 1px solid rgba(223, 214, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
}

.stage-modal-summary-card {
  padding: 20px;
  border-radius: 22px;
  border: 1px solid rgba(196, 206, 228, 0.08);
  background: rgba(255, 255, 255, 0.03);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
  animation: stage-modal-rise 260ms ease both;
}

.stage-summary-inline-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.stage-modal-summary-pills {
  grid-template-columns: repeat(3, max-content);
  align-items: center;
}

.stage-summary-list {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.stage-summary-list div {
  display: grid;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(196, 206, 228, 0.08);
  background: rgba(255, 255, 255, 0.025);
}

.stage-summary-list strong {
  color: var(--text-main);
  line-height: 1.35;
}

.stage-modal-checklist {
  list-style: none;
  padding: 0;
  margin: 0;
}

.stage-modal-checklist li {
  position: relative;
  padding-left: 22px;
}

.stage-modal-checklist li::before {
  content: '';
  position: absolute;
  top: 8px;
  left: 0;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
}

.stage-modal-checklist li.is-ready::before {
  background: #8cf3e8;
  box-shadow: 0 0 0 6px rgba(140, 243, 232, 0.12);
}

.stage-modal-footer {
  width: 100%;
  grid-template-columns: 1fr auto;
  align-items: center;
}

.stage-form-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-top: 6px;
  padding: 16px 0 8px;
  border-top: 1px solid rgba(196, 206, 228, 0.08);
}

.stage-form-actions .workspace-button {
  width: auto;
  min-width: 148px;
}

@keyframes stage-modal-rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.workspace-inline-check--single {
  width: fit-content;
}

.workspace-inline-check--compact {
  white-space: nowrap;
}

.workspace-actions--split-top {
  margin-top: 12px;
}

.workspace-preview-field span {
  color: #f59e0b;
}

.workspace-table-shell {
  overflow: auto;
}

.stages-table {
  min-width: 1180px;
}

.workspace-table {
  width: 100%;
  border-collapse: collapse;
}

.workspace-table th,
.workspace-table td {
  padding: 14px 12px;
  border-bottom: 1px solid rgba(223, 214, 255, 0.08);
  text-align: left;
  color: var(--text-soft);
  vertical-align: top;
}

.workspace-table th {
  color: var(--text-main);
  font-size: 0.75rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

@media (max-width: 1100px) {
  .workspace-two-column,
  .workspace-advanced-grid,
  .stage-availability-picker,
  .stage-toggle-grid,
  .stage-code-option-grid,
  .stage-modal__grid--base,
  .stage-modal__grid--rules,
  .stage-tax-card__grid,
  .stage-image-row,
  .stage-summary-inline-grid {
    grid-template-columns: 1fr;
  }

  .stage-modal__field--hero,
  .stage-tax-card__included {
    grid-column: span 1;
  }

  .stage-base-facts,
  .stage-access-facts {
    gap: 8px;
  }
}

@media (max-width: 900px) {
  .workspace-panel__header--spread,
  .workspace-subpanel__head,
  .workspace-assignment-card,
  .workspace-limit-card__header,
  .stage-modal__section-head,
  .stage-code-shell__head,
  .stage-modal-footer {
    display: grid;
  }

  .stage-modal-summary-pills,
  .stage-access-facts {
    grid-template-columns: 1fr;
  }

  .stage-activation-row {
    display: grid;
    justify-items: start;
  }

  .stage-summary-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .stage-scope-picker {
    display: flex;
    overflow-x: auto;
    padding-bottom: 4px;
    margin-right: -6px;
    padding-right: 6px;
    scroll-snap-type: x proximity;
  }

  .stage-scope-pill {
    flex: 0 0 auto;
    scroll-snap-align: start;
  }

  .stage-modal-footer {
    grid-template-columns: 1fr;
  }

  .stage-form-actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .stages-table thead {
    display: none;
  }

  .stages-table,
  .stages-table tbody,
  .stages-table tr,
  .stages-table td {
    display: block;
    width: 100%;
  }

  .stages-table {
    min-width: 0;
    border: none;
    background: transparent;
  }

  .stages-table tr {
    margin-bottom: 16px;
    padding: 16px;
    border-radius: 22px;
    border: 1px solid rgba(148, 163, 184, 0.18);
    background: linear-gradient(180deg, rgba(13, 20, 37, 0.98), rgba(8, 13, 24, 0.96));
    box-shadow: 0 18px 34px rgba(2, 6, 23, 0.28);
  }

  .stages-table td {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 0;
    border-bottom: 1px solid rgba(223, 214, 255, 0.08);
  }

  .stages-table td:last-child {
    border-bottom: none;
  }

  .stages-table td::before {
    content: attr(data-label);
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(208, 219, 238, 0.68);
    font-weight: 700;
    min-width: 104px;
    flex-shrink: 0;
  }

  .stage-summary-cell,
  .stage-link-cell,
  .stage-status-cell,
  .stage-actions-cell {
    display: block;
  }

  .stage-summary-cell::before,
  .stage-link-cell::before,
  .stage-status-cell::before,
  .stage-actions-cell::before {
    display: block;
    margin-bottom: 10px;
  }

  .stage-summary-cell {
    padding-top: 0;
  }

  .stage-summary-cell .workspace-table-title {
    font-size: 1.04rem;
    line-height: 1.15;
  }

  .stage-summary-cell .workspace-table-subcopy {
    margin-top: 6px;
  }

  .stage-link-cell .workspace-inline-link,
  .stage-link-cell .workspace-table-subcopy {
    display: inline-flex;
    max-width: 100%;
  }

  .stage-status-cell .workspace-status-pill {
    display: inline-flex;
  }

  .stage-actions-cell {
    min-width: 0;
    display: block;
  }

  .stage-table-actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .stage-table-actions .workspace-chip-button {
    width: 100%;
    justify-content: center;
  }

  .stage-modal-footer .workspace-button {
    width: 100%;
  }

  .stage-form-actions .workspace-button {
    width: 100%;
  }
}

@media (max-width: 640px) {
  .stage-summary-list {
    grid-template-columns: 1fr;
  }
}
</style>
